/* ==========================================================================
   Volt theme: vanilla JavaScript (no dependencies, no tracking)
   Drawers, header, mega menu, carousels, tabs, accordions, countdowns, cart,
   product page, quick view, predictive search, collection filtering, toasts.
   ========================================================================== */
(function () {
  'use strict';

  var V = (window.volt = window.volt || {});
  var routes = V.routes || {};
  var strings = V.strings || {};
  var doc = document;
  var root = doc.documentElement;
  var body = doc.body;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Helpers ---------- */
  function $(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function once(el, key) {
    var k = '_v_' + key;
    if (el[k]) return false;
    el[k] = true;
    return true;
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); }
  function money(cents) {
    var value = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(root.lang || undefined, { style: 'currency', currency: V.currency || 'USD' }).format(value);
    } catch (e) {
      return value.toFixed(2);
    }
  }
  function announce(msg) {
    var live = $('[data-live-region]');
    if (!live || !msg) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 50);
  }
  function jsonHeaders() { return { 'Content-Type': 'application/json', Accept: 'application/json' }; }
  function store(key, value) {
    try {
      if (value === undefined) return JSON.parse(localStorage.getItem(key) || 'null');
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* storage unavailable */ }
    return null;
  }
  function focusable(container) {
    return $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null || el === doc.activeElement; });
  }

  /* ---------- Drawers ---------- */
  var openDrawerEl = null;
  var lastTrigger = null;
  var overlay = $('[data-drawer-overlay]');

  function lockScroll(lock) {
    if (lock) {
      var sw = window.innerWidth - root.clientWidth;
      body.style.overflow = 'hidden';
      if (sw > 0) body.style.paddingRight = sw + 'px';
    } else {
      body.style.overflow = '';
      body.style.paddingRight = '';
    }
  }

  function openDrawer(name, trigger) {
    var drawer = $('[data-drawer="' + name + '"]');
    if (!drawer) return false;
    if (openDrawerEl && openDrawerEl !== drawer) closeDrawer(true);
    lastTrigger = trigger || doc.activeElement;
    openDrawerEl = drawer;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    if (overlay) {
      overlay.hidden = false;
      requestAnimationFrame(function () { overlay.classList.add('is-visible'); });
    }
    lockScroll(true);
    $$('[aria-controls="' + drawer.id + '"][aria-haspopup]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    setTimeout(function () {
      var target = $('[data-search-input]', drawer) || $('[data-drawer-close]', drawer) || focusable(drawer)[0];
      if (target) target.focus({ preventScroll: true });
    }, 60);
    drawer.dispatchEvent(new CustomEvent('volt:drawer:open', { bubbles: true }));
    return true;
  }

  function closeDrawer(skipFocus) {
    var drawer = openDrawerEl;
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    $$('[aria-controls="' + drawer.id + '"][aria-haspopup]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (overlay) {
      overlay.classList.remove('is-visible');
      setTimeout(function () { if (!openDrawerEl) overlay.hidden = true; }, 350);
    }
    openDrawerEl = null;
    lockScroll(false);
    if (drawer.getAttribute('data-drawer') === 'menu') resetMenu();
    if (!skipFocus && lastTrigger && lastTrigger.focus) lastTrigger.focus({ preventScroll: true });
    drawer.dispatchEvent(new CustomEvent('volt:drawer:close', { bubbles: true }));
  }

  doc.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      var inLink = e.target.closest('a[href]');
      if (!inLink) e.preventDefault();
      closeDrawer();
      return;
    }
    if (overlay && e.target === overlay) closeDrawer();
    // Click on the backdrop area of a modal drawer
    var modal = e.target.closest('.drawer--modal');
    if (modal && e.target === modal) closeDrawer();
  });

  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (openDrawerEl) { closeDrawer(); return; }
      var facets = $('#FacetsForm.is-open');
      if (facets) { closeFacets(); return; }
      var lb = $('[data-lightbox]:not([hidden])');
      if (lb && lb._close) { lb._close(); return; }
      var vf = $('[data-video-frame]:not([hidden])');
      if (vf && vf._close) vf._close();
      $$('[data-nav-item].is-open').forEach(function (i) { setNav(i, false); });
    }
    if (e.key === 'Tab' && openDrawerEl) {
      var items = focusable(openDrawerEl);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Header ---------- */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var wrapper = header.closest('.shopify-section') || header;
    var mode = header.getAttribute('data-sticky');
    var lastY = window.scrollY;

    function setHeights() {
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
      var ann = $('[data-announcement]');
      root.style.setProperty('--announcement-height', ann ? ann.offsetHeight + 'px' : '0px');
    }
    setHeights();
    if (window.ResizeObserver) new ResizeObserver(setHeights).observe(header);
    else window.addEventListener('resize', debounce(setHeights, 150));

    if (!once(header, 'scroll')) return;
    var ticking = false;
    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 10);
      if (mode === 'scroll-up') {
        var busy = openDrawerEl || $('[data-nav-item].is-open') || header.matches(':focus-within');
        if (y > header.offsetHeight + 80 && y > lastY + 4 && !busy) header.classList.add('is-hidden');
        else if (y < lastY - 4 || y <= header.offsetHeight) header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
    onScroll();
    wrapper.classList.add('header-wrapper');
  }

  /* Mega menu / dropdowns */
  function setNav(item, open) {
    item.classList.toggle('is-open', open);
    var toggle = $('[data-nav-toggle]', item);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function initNav() {
    $$('[data-nav-item]').forEach(function (item) {
      if (!once(item, 'nav')) return;
      var timer;
      var canHover = window.matchMedia('(hover: hover)').matches;
      var toggle = $('[data-nav-toggle]', item);
      if (toggle) {
        toggle.addEventListener('click', function () {
          var open = !item.classList.contains('is-open');
          $$('[data-nav-item].is-open').forEach(function (o) { if (o !== item) setNav(o, false); });
          setNav(item, open);
        });
      }
      if (canHover) {
        item.addEventListener('mouseenter', function () {
          clearTimeout(timer);
          $$('[data-nav-item].is-open').forEach(function (o) { if (o !== item) setNav(o, false); });
          if ($('[data-nav-panel]', item)) setNav(item, true);
        });
        item.addEventListener('mouseleave', function () {
          timer = setTimeout(function () { setNav(item, false); }, 140);
        });
      }
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) setNav(item, false);
      });
    });
    if (once(doc, 'navOutside')) {
      doc.addEventListener('click', function (e) {
        if (!e.target.closest('[data-nav-item]')) $$('[data-nav-item].is-open').forEach(function (i) { setNav(i, false); });
      });
    }
  }

  /* Multi-level mobile menu */
  function resetMenu() {
    $$('[data-menu-panel]').forEach(function (p, i) {
      p.classList.toggle('is-active', i === 0);
      p.classList.remove('is-behind');
      if (i > 0) p.setAttribute('aria-hidden', 'true');
    });
    $$('[data-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
  }
  doc.addEventListener('click', function (e) {
    var open = e.target.closest('[data-menu-open]');
    if (open) {
      var next = doc.getElementById(open.getAttribute('data-menu-open'));
      var cur = open.closest('[data-menu-panel]');
      if (!next) return;
      next._parent = cur;
      cur.classList.remove('is-active');
      cur.classList.add('is-behind');
      next.classList.add('is-active');
      next.setAttribute('aria-hidden', 'false');
      open.setAttribute('aria-expanded', 'true');
      var f = focusable(next)[0];
      if (f) setTimeout(function () { f.focus({ preventScroll: true }); }, 200);
      return;
    }
    var back = e.target.closest('[data-menu-back]');
    if (back) {
      var panel = back.closest('[data-menu-panel]');
      var parent = panel._parent;
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
      if (parent) {
        parent.classList.remove('is-behind');
        parent.classList.add('is-active');
        var opener = $('[data-menu-open="' + panel.id + '"]', parent);
        if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus({ preventScroll: true }); }
      }
    }
  });

  /* ---------- Slideshow (hero + announcement) ---------- */
  function createSlideshow(rootEl, o) {
    var slides = $$(o.slide, rootEl);
    if (slides.length < 2) return null;
    var dots = o.dot ? $$(o.dot, rootEl) : [];
    var index = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('is-active'); }));
    var timer = null;
    var delay = o.delay || 0;
    var paused = false;
    var userPaused = false;

    function go(i, user) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) {
        var on = n === index;
        s.classList.toggle('is-active', on);
        if (o.hideInactive) s.setAttribute('aria-hidden', on ? 'false' : 'true');
        $$('a, button', s).forEach(function (el) { if (on) el.removeAttribute('tabindex'); else el.setAttribute('tabindex', '-1'); });
      });
      dots.forEach(function (d, n) {
        d.classList.toggle('is-active', n === index);
        d.setAttribute('aria-selected', n === index ? 'true' : 'false');
      });
      if (user) restart();
    }
    function tick() { if (!paused && !userPaused && !doc.hidden) go(index + 1); }
    function restart() {
      clearInterval(timer);
      if (delay > 0 && !reduceMotion && !userPaused) timer = setInterval(tick, delay);
    }
    $$(o.prev, rootEl).forEach(function (b) { b.addEventListener('click', function () { go(index - 1, true); }); });
    $$(o.next, rootEl).forEach(function (b) { b.addEventListener('click', function () { go(index + 1, true); }); });
    dots.forEach(function (d, n) { d.addEventListener('click', function () { go(n, true); }); });
    var toggle = o.toggle ? $(o.toggle, rootEl) : null;
    if (toggle) {
      toggle.addEventListener('click', function () {
        userPaused = !userPaused;
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        if (userPaused) clearInterval(timer); else restart();
      });
    }
    rootEl.addEventListener('mouseenter', function () { paused = true; });
    rootEl.addEventListener('mouseleave', function () { paused = false; });
    rootEl.addEventListener('focusin', function () { paused = true; });
    rootEl.addEventListener('focusout', function () { paused = false; });
    // swipe
    var sx = null;
    rootEl.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
    rootEl.addEventListener('touchend', function (e) {
      if (sx === null) return;
      var dx = e.changedTouches[0].clientX - sx;
      if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1), true);
      sx = null;
    }, { passive: true });
    rootEl.addEventListener('keydown', function (e) {
      if (e.target.closest('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') go(index - 1, true);
      if (e.key === 'ArrowRight') go(index + 1, true);
    });
    go(index);
    restart();
    return { go: go, slides: slides };
  }

  function initSlideshows(ctx) {
    $$('[data-slideshow]', ctx).forEach(function (el) {
      if (!once(el, 'slideshow')) return;
      var ms = parseInt(el.getAttribute('data-autoplay'), 10) || 0;
      el._slideshow = createSlideshow(el, { slide: '[data-slide]', dot: '[data-slideshow-dot]', prev: '[data-slideshow-prev]', next: '[data-slideshow-next]', toggle: '[data-slideshow-toggle]', delay: ms, hideInactive: true });
    });
    $$('[data-announcement]', ctx).forEach(function (el) {
      if (!once(el, 'announcement')) return;
      var auto = el.getAttribute('data-autoplay') === 'true';
      var ms = parseInt(el.getAttribute('data-delay'), 10) || 5000;
      el._slideshow = createSlideshow(el, { slide: '[data-announcement-slide]', prev: '[data-announcement-prev]', next: '[data-announcement-next]', delay: auto ? ms : 0 });
    });
  }

  /* ---------- Carousels (scroll-snap) ---------- */
  function initCarousels(ctx) {
    $$('[data-carousel]', ctx).forEach(function (el) {
      if (!once(el, 'carousel')) return;
      var track = $('[data-carousel-track]', el);
      var prev = $('[data-carousel-prev]', el);
      var next = $('[data-carousel-next]', el);
      if (!track) return;
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        var scrollable = max > 0;
        [prev, next].forEach(function (b) { if (b) b.hidden = !scrollable; });
        if (prev) { prev.disabled = track.scrollLeft <= 2; }
        if (next) { next.disabled = track.scrollLeft >= max; }
      }
      function by(dir) {
        track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      if (prev) prev.addEventListener('click', function () { by(-1); });
      if (next) next.addEventListener('click', function () { by(1); });
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      if (window.ResizeObserver) new ResizeObserver(update).observe(track);
      if (window.MutationObserver) new MutationObserver(update).observe(track, { childList: true });
      update();
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(ctx) {
    $$('[data-tabs]', ctx).forEach(function (el) {
      if (!once(el, 'tabs')) return;
      var tabs = $$('.tabs__tab', el);
      var panels = $$('.tabs__panel', el);
      var indicator = $('.tabs__indicator', el);
      function place() {
        var active = $('.tabs__tab.is-active', el);
        if (!active || !indicator) return;
        indicator.style.width = active.offsetWidth + 'px';
        indicator.style.transform = 'translateX(' + active.offsetLeft + 'px)';
      }
      function activate(i, focus) {
        tabs.forEach(function (t, n) {
          var on = n === i;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (panels[n]) {
            panels[n].classList.toggle('is-active', on);
            panels[n].hidden = !on;
          }
        });
        if (focus) tabs[i].focus();
        place();
        initCarousels(panels[i]);
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { activate(i); });
        t.addEventListener('keydown', function (e) {
          var n = null;
          if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') n = 0;
          if (e.key === 'End') n = tabs.length - 1;
          if (n !== null) { e.preventDefault(); activate(n, true); }
        });
      });
      el._tabs = { activate: activate };
      window.addEventListener('resize', debounce(place, 150));
      if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(place);
      place();
    });
  }

  /* ---------- Accordion ---------- */
  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest('.accordion__trigger');
    if (!trigger) return;
    var panel = doc.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    var open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
    panel.style.overflow = 'hidden';
    if (reduceMotion) { panel.hidden = open; panel.style.height = ''; return; }
    if (open) {
      panel.style.height = panel.scrollHeight + 'px';
      void panel.offsetHeight;
      panel.style.height = '0px';
      var done = function () { panel.hidden = true; panel.style.height = ''; panel.removeEventListener('transitionend', done); };
      panel.addEventListener('transitionend', done);
      setTimeout(done, 450);
    } else {
      panel.hidden = false;
      var h = panel.scrollHeight;
      panel.style.height = '0px';
      void panel.offsetHeight;
      panel.style.height = h + 'px';
      var end = function () { panel.style.height = ''; panel.style.overflow = ''; panel.removeEventListener('transitionend', end); };
      panel.addEventListener('transitionend', end);
      setTimeout(end, 450);
    }
  });

  /* ---------- Countdown (real merchant dates only) ---------- */
  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function initCountdowns(ctx) {
    $$('[data-countdown]', ctx).forEach(function (el) {
      if (!once(el, 'countdown')) return;
      var end = parseInt(el.getAttribute('data-countdown'), 10) * 1000;
      if (!end) return;
      var parts = {
        d: $('[data-days]', el), h: $('[data-hours]', el), m: $('[data-minutes]', el), s: $('[data-seconds]', el)
      };
      var timer;
      function render() {
        var left = Math.max(0, end - Date.now());
        var s = Math.floor(left / 1000);
        if (parts.d) parts.d.textContent = pad(Math.floor(s / 86400));
        if (parts.h) parts.h.textContent = pad(Math.floor((s % 86400) / 3600));
        if (parts.m) parts.m.textContent = pad(Math.floor((s % 3600) / 60));
        if (parts.s) parts.s.textContent = pad(s % 60);
        if (left <= 0) {
          clearInterval(timer);
          var msg = el.parentElement && $('[data-countdown-expired]', el.parentElement);
          el.hidden = true;
          if (msg) msg.hidden = false;
          var urgency = el.closest('[data-urgency]');
          if (urgency) urgency.hidden = true;
          if (el.getAttribute('data-hide-section') === 'true') {
            var sec = el.closest('[data-flash-sale]') || el.closest('.shopify-section');
            if (sec) sec.hidden = true;
          }
        }
      }
      render();
      timer = setInterval(render, 1000);
    });
  }

  /* ---------- Scroll reveal + parallax ---------- */
  var revealOn = body.getAttribute('data-reveal-enabled') !== 'false' && !reduceMotion && 'IntersectionObserver' in window;
  var revealObserver = null;
  if (revealOn) {
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-revealed'); revealObserver.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  }
  function initReveal(ctx) {
    $$('[data-reveal]:not(.is-revealed)', ctx).forEach(function (el) {
      if (revealObserver) revealObserver.observe(el); else el.classList.add('is-revealed');
    });
  }

  var parallaxEls = [];
  function initParallax(ctx) {
    if (body.getAttribute('data-parallax-enabled') !== 'true' || reduceMotion) return;
    $$('[data-parallax]', ctx).forEach(function (el) {
      if (parallaxEls.indexOf(el) === -1) { parallaxEls.push(el); el.style.willChange = 'transform'; }
    });
  }
  (function () {
    var tick = false;
    function run() {
      var vh = window.innerHeight;
      parallaxEls = parallaxEls.filter(function (el) { return doc.body.contains(el); });
      parallaxEls.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        var offset = (r.top + r.height / 2 - vh / 2) * -0.08;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0) scale(1.12)';
      });
      tick = false;
    }
    window.addEventListener('scroll', function () {
      if (parallaxEls.length && !tick) { tick = true; requestAnimationFrame(run); }
    }, { passive: true });
  })();

  /* ---------- Quantity selector ---------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    var wrap = btn.closest('[data-qty]');
    var input = $('.qty__input', wrap);
    if (!input) return;
    var min = parseInt(input.min || '1', 10);
    var maxAttr = input.getAttribute('max');
    var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    var cur = parseInt(input.value, 10) || 0;
    var next = btn.hasAttribute('data-qty-minus') ? cur - 1 : cur + 1;
    next = Math.min(max, Math.max(min, next));
    if (next === cur) return;
    input.value = next;
    wrap.classList.add('is-bumped');
    setTimeout(function () { wrap.classList.remove('is-bumped'); }, 320);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ==========================================================================
     CART
     ========================================================================== */
  var drawerExists = !!$('[data-drawer="cart"]');
  var cartMode = V.cartType === 'drawer' && drawerExists ? 'drawer' : 'page';

  function cartRequest(url, payload) {
    return fetch(url, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) { var err = new Error(data.description || data.message || strings.error); err.data = data; throw err; }
        return data;
      });
    });
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = !count;
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  }

  function swapRender(newDoc, key) {
    var fresh = $('[data-cart-render="' + key + '"]', newDoc);
    var old = $('[data-cart-render="' + key + '"]');
    if (fresh && old) {
      var scrollEl = $('.cart-drawer__body', old);
      var st = scrollEl ? scrollEl.scrollTop : 0;
      old.innerHTML = fresh.innerHTML;
      var ns = $('.cart-drawer__body', old);
      if (ns) ns.scrollTop = st;
    }
  }

  function refreshCart() {
    var tasks = [fetch(routes.cartJson, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })];
    if ($('[data-cart-render="cart-drawer"]')) {
      tasks.push(fetch(routes.root + '?sections=cart-drawer').then(function (r) { return r.json(); }));
    } else tasks.push(null);
    if ($('[data-cart-render="main-cart"]')) {
      tasks.push(fetch(routes.cart).then(function (r) { return r.text(); }));
    } else tasks.push(null);
    return Promise.all(tasks).then(function (res) {
      var cart = res[0];
      if (res[1] && res[1]['cart-drawer']) swapRender(parseHTML(res[1]['cart-drawer']), 'cart-drawer');
      if (res[2]) swapRender(parseHTML(res[2]), 'main-cart');
      updateCount(cart.item_count);
      initShipBars();
      initUpsells();
      initReveal();
      doc.dispatchEvent(new CustomEvent('volt:cart:updated', { detail: cart }));
      return cart;
    });
  }

  function cartAdd(payload) {
    return fetch(routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: payload }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.status) { var err = new Error(data.description || data.message || strings.error); throw err; }
        return data;
      });
    });
  }

  function afterAdd() {
    return refreshCart().then(function () {
      if ($('[data-cart-page]')) return;
      if (cartMode === 'drawer') openDrawer('cart');
      else window.location.href = routes.cart;
    });
  }

  function showFormError(form, msg) {
    var slot = $('[data-product-error]', form.closest('[data-product-section]') || form) || null;
    if (slot) { slot.textContent = msg; slot.hidden = false; return; }
    var p = form.nextElementSibling;
    if (!p || !p.classList.contains('quick-add-error')) {
      p = doc.createElement('p');
      p.className = 'form-status form-status--error quick-add-error';
      p.setAttribute('role', 'alert');
      form.parentNode.insertBefore(p, form.nextSibling);
    }
    p.textContent = msg;
  }
  function clearFormError(form) {
    var slot = $('[data-product-error]', form.closest('[data-product-section]') || form);
    if (slot) slot.hidden = true;
    var p = form.nextElementSibling;
    if (p && p.classList.contains('quick-add-error')) p.remove();
  }

  function setBusy(btn, busy, done) {
    if (!btn) return;
    btn.classList.toggle('is-loading', busy);
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) { btn.setAttribute('aria-disabled', 'true'); }
    else btn.removeAttribute('aria-disabled');
    if (done) {
      btn.classList.add('is-added');
      var label = $('[data-add-label]', btn);
      var prev = label ? label.textContent : '';
      if (label) label.textContent = strings.added;
      setTimeout(function () {
        btn.classList.remove('is-added');
        if (label && label.textContent === strings.added) label.textContent = prev || strings.addToCart;
      }, 1800);
    }
  }

  doc.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Confirm dialogs (address delete etc.)
    var msg = form.getAttribute('data-confirm');
    if (msg && !window.confirm(msg)) { e.preventDefault(); return; }

    // Product + quick add forms
    if (form.hasAttribute('data-product-form') || form.hasAttribute('data-quick-add-form')) {
      e.preventDefault();
      var btn = e.submitter && e.submitter.name === 'add' ? e.submitter : $('[data-add-button]', form) || e.submitter;
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      if (btn && btn.disabled) return;
      var fd = new FormData(form);
      // Include fields associated by the form attribute (quantity, properties)
      if (form.id) $$('[form="' + form.id + '"]').forEach(function (el) { if (el.name && !fd.has(el.name) && !el.disabled && (el.type !== 'radio' || el.checked)) fd.append(el.name, el.value); });
      if (!fd.get('quantity')) fd.set('quantity', 1);
      fd.delete('add');
      clearFormError(form);
      setBusy(btn, true);
      cartAdd(fd).then(function () {
        return afterAdd();
      }).then(function () {
        setBusy(btn, false, true);
        announce(strings.added);
      }).catch(function (err) {
        setBusy(btn, false);
        showFormError(form, err.message || strings.error);
      });
      return;
    }

    // Cart form in drawer: Enter in quantity fields must not leave the page
    if (form.id && form.id.indexOf('CartForm') === 0) {
      var submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') { e.preventDefault(); }
    }
  });

  // Buy it now
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-buy-now]');
    if (!btn) return;
    var form = btn.closest('[data-product-section]') && $('[data-product-form]', btn.closest('[data-product-section]'));
    if (!form || btn.disabled) return;
    var fd = new FormData(form);
    if (form.id) $$('[form="' + form.id + '"]').forEach(function (el) { if (el.name && !fd.has(el.name) && !el.disabled && (el.type !== 'radio' || el.checked)) fd.append(el.name, el.value); });
    fd.delete('add');
    if (!fd.get('quantity')) fd.set('quantity', 1);
    btn.classList.add('is-loading');
    cartAdd(fd).then(function () { window.location.href = '/checkout'; }).catch(function (err) {
      btn.classList.remove('is-loading');
      showFormError(form, err.message || strings.error);
    });
  });

  // Cart line changes
  function changeLine(line, quantity, itemEl) {
    var host = itemEl.closest('.cart-drawer') || itemEl.closest('[data-cart-page]');
    if (host) host.classList.add('is-updating');
    if (quantity === 0) itemEl.classList.add('is-removing');
    var err = $('[data-cart-error]', itemEl);
    if (err) err.hidden = true;
    return cartRequest(routes.cartChange, { line: line, quantity: quantity }).then(function (cart) {
      return refreshCart().then(function () {
        var item = cart.items[line - 1];
        if (item && quantity > 0 && item.quantity < quantity) {
          var again = $('[data-cart-item][data-line="' + line + '"] [data-cart-error]');
          if (again) { again.textContent = strings.error; again.hidden = false; }
        }
      });
    }).catch(function (e) {
      itemEl.classList.remove('is-removing');
      if (err) { err.textContent = e.message || strings.error; err.hidden = false; }
    }).then(function () {
      if (host) host.classList.remove('is-updating');
    });
  }

  doc.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-cart-remove]');
    if (!rm) return;
    e.preventDefault();
    var item = rm.closest('[data-cart-item]');
    changeLine(parseInt(rm.getAttribute('data-line'), 10), 0, item);
  });
  var qtyChange = debounce(function (input) {
    var item = input.closest('[data-cart-item]');
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(parseInt(input.getAttribute('data-line'), 10), qty, item);
  }, 350);
  doc.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (input) qtyChange(input);
  });

  // Gift message + note
  function saveCartMeta(data) {
    return cartRequest(routes.cartUpdate, data).catch(function () { /* non-blocking */ });
  }
  var noteSave = debounce(function (v) { saveCartMeta({ note: v }); }, 600);
  var giftSave = debounce(function (v) { saveCartMeta({ attributes: { 'Gift message': v } }); }, 600);
  doc.addEventListener('input', function (e) {
    if (e.target.matches('[data-cart-note]')) noteSave(e.target.value);
    if (e.target.matches('[data-gift-message]')) giftSave(e.target.value);
  });
  doc.addEventListener('change', function (e) {
    var t = e.target.closest('[data-gift-toggle]');
    if (!t) return;
    var field = $('.gift__field', t.closest('[data-gift]'));
    var ta = $('[data-gift-message]', field);
    field.hidden = !t.checked;
    if (t.checked) ta.focus();
    else { ta.value = ''; saveCartMeta({ attributes: { 'Gift message': '' } }); }
  });

  // Cart links open the drawer
  doc.addEventListener('click', function (e) {
    var link = e.target.closest('[data-cart-link]');
    if (!link || cartMode !== 'drawer') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    if ($('[data-cart-page]')) return;
    e.preventDefault();
    openDrawer('cart', link);
  });

  // Free shipping progress (amounts are formatted in the visitor's currency)
  function initShipBars() {
    $$('[data-free-ship]').forEach(function (bar) {
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
      var total = parseInt(bar.getAttribute('data-total'), 10) || 0;
      if (!threshold) return;
      var text = $('[data-free-ship-text]', bar);
      var fill = $('[data-free-ship-fill]', bar);
      var track = $('[data-free-ship-track]', bar);
      var pct = Math.min(100, Math.round((total / threshold) * 100));
      var complete = total >= threshold;
      bar.classList.toggle('is-complete', complete);
      if (text && total > 0) {
        text.textContent = complete ? strings.freeShippingReached : (strings.freeShippingRemaining || '').replace('[amount]', money(threshold - total));
      }
      if (fill) fill.style.width = pct + '%';
      if (track) track.setAttribute('aria-valuenow', String(pct));
    });
  }

  // Cart upsell via Product Recommendations API
  function initUpsells() {
    $$('[data-cart-upsell]').forEach(function (el) {
      if (el._loaded) return;
      var pid = el.getAttribute('data-product-id');
      if (!pid || !routes.recommendations) return;
      el._loaded = true;
      var url = routes.recommendations + '?section_id=cart-upsell&product_id=' + encodeURIComponent(pid) + '&limit=' + (el.getAttribute('data-limit') || 6) + '&intent=related';
      fetch(url).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
        if (!html) return;
        var d = parseHTML(html);
        var list = $('[data-upsell-list]', d);
        if (list) el.innerHTML = list.outerHTML;
      }).catch(function () { /* optional content */ });
    });
  }

  /* ==========================================================================
     PRODUCT
     ========================================================================== */
  function initProduct(section) {
    if (!once(section, 'product')) return;
    var dataEl = $('[data-product-json]', section);
    if (!dataEl) return;
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }
    V.currency = V.currency || product.currency;
    var isQuick = section.hasAttribute('data-quick-view');
    var form = $('[data-product-form]', section);
    var variantInput = $('[data-variant-id]', section);
    var gallery = $('[data-gallery]', section);
    var picker = $('[data-variant-picker]', section);

    function selectedOptions() {
      var vals = [];
      $$('[data-option]', section).forEach(function (fs) {
        var idx = parseInt(fs.getAttribute('data-option-index'), 10) - 1;
        var sel = $('[data-option-select]', fs);
        var checked = $('[data-option-input]:checked', fs);
        vals[idx] = sel ? sel.value : (checked ? checked.value : null);
      });
      return vals;
    }
    function findVariant(vals) {
      return product.variants.filter(function (v) {
        return v.options.every(function (o, i) { return vals[i] === o; });
      })[0] || null;
    }
    function currentVariant() {
      var id = variantInput && parseInt(variantInput.value, 10);
      return product.variants.filter(function (v) { return v.id === id; })[0] || product.variants[0];
    }

    function markAvailability() {
      var vals = selectedOptions();
      $$('[data-option]', section).forEach(function (fs) {
        var idx = parseInt(fs.getAttribute('data-option-index'), 10) - 1;
        $$('[data-option-input]', fs).forEach(function (inp) {
          var test = vals.slice();
          test[idx] = inp.value;
          var exists = product.variants.some(function (v) { return v.available && v.options.every(function (o, i) { return test[i] === o; }); });
          var label = $('label[for="' + inp.id + '"]', fs);
          if (label) label.classList.toggle('is-unavailable', !exists);
        });
        $$('[data-option-select] option', fs).forEach(function (opt) {
          var test = vals.slice();
          test[idx] = opt.value;
          var exists = product.variants.some(function (v) { return v.available && v.options.every(function (o, i) { return test[i] === o; }); });
          opt.classList.toggle('is-unavailable', !exists);
        });
        var out = $('[data-option-selected]', fs);
        if (out && vals[idx]) out.textContent = vals[idx];
      });
    }

    function setPrice(v) {
      var wrap = $('[data-price]', section);
      if (!wrap || !v) return;
      var onSale = v.compare > v.price;
      wrap.classList.toggle('price--on-sale', onSale);
      var cur = $('[data-price-current]', wrap);
      var cmp = $('[data-price-compare]', wrap);
      var off = $('[data-price-off]', wrap);
      if (cur) cur.textContent = v.priceFormatted;
      if (cmp) { cmp.textContent = v.compareFormatted; cmp.hidden = !onSale; }
      if (off) {
        off.hidden = !onSale;
        if (onSale) off.innerHTML = '&minus;' + Math.round((v.compare - v.price) / v.compare * 100) + '%';
      }
    }

    function setStock(v) {
      var el = $('[data-stock]', section);
      if (!el) return;
      var low = parseInt(el.getAttribute('data-low-threshold'), 10) || 5;
      var showCount = el.getAttribute('data-show-count') === 'true';
      var state = 'in';
      var text = strings.stockIn;
      if (!v) { state = 'out'; text = strings.unavailable; }
      else if (!v.available) { state = 'out'; text = strings.soldOut; }
      else if (v.tracked && v.policy === 'deny' && v.inventory > 0 && v.inventory <= low) {
        state = 'low';
        text = showCount ? (strings.stockLowCount || '').replace('99999', v.inventory) : strings.stockLow;
      }
      el.className = el.className.replace(/stock--(in|low|out)/g, '').trim() + ' stock--' + state;
      var t = $('[data-stock-text]', el);
      if (t) t.textContent = text;
    }

    function setButtons(v) {
      var label = v ? (v.available ? strings.addToCart : strings.soldOut) : strings.unavailable;
      $$('[data-add-button], [data-buy-now], [data-sticky-add]', section).forEach(function (b) {
        b.disabled = !v || !v.available;
      });
      var l = $('[data-add-label]', section);
      if (l) l.textContent = label;
      var sl = $('[data-sticky-label]', section);
      if (sl) sl.textContent = label;
      var sp = $('[data-sticky-price]', section);
      if (sp && v) sp.textContent = v.priceFormatted;
      var ss = $('[data-sticky-variant]', section);
      if (ss && v) ss.value = String(v.id);
    }

    function setSku(v) {
      var wrap = $('[data-sku-wrap]', section);
      var sku = $('[data-sku]', section);
      if (!wrap || !sku) return;
      sku.textContent = v ? v.sku : '';
      wrap.hidden = !v || !v.sku;
    }

    function update(opts) {
      var v = findVariant(selectedOptions());
      markAvailability();
      setPrice(v);
      setStock(v);
      setButtons(v);
      setSku(v);
      if (variantInput && v) variantInput.value = v.id;
      if (v && !isQuick && !(opts && opts.silent) && window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u.toString());
      }
      if (v && v.mediaId && gallery && gallery._showMedia && !(opts && opts.silent)) gallery._showMedia(String(v.mediaId));
      if (form) clearFormError(form);
      refreshVolume();
      section.dispatchEvent(new CustomEvent('volt:variant:change', { bubbles: true, detail: { variant: v } }));
    }

    if (picker) {
      picker.addEventListener('change', function (e) {
        if (e.target.matches('[data-option-input], [data-option-select]')) update();
      });
    }

    // Sticky add to cart: variant select drives the main picker
    var sticky = $('[data-sticky-variant]', section);
    if (sticky) {
      sticky.addEventListener('change', function () {
        var v = product.variants.filter(function (x) { return String(x.id) === sticky.value; })[0];
        if (!v) return;
        $$('[data-option]', section).forEach(function (fs) {
          var idx = parseInt(fs.getAttribute('data-option-index'), 10) - 1;
          var sel = $('[data-option-select]', fs);
          if (sel) sel.value = v.options[idx];
          else $$('[data-option-input]', fs).forEach(function (inp) { inp.checked = inp.value === v.options[idx]; });
        });
        update();
      });
    }

    // Volume offers
    var vol = $('[data-volume-offers]', section);
    var qtyInput = $('.qty__input[name="quantity"]', section) || (form && form.id && $('[name="quantity"][form="' + form.id + '"]'));
    function refreshVolume() {
      if (!vol) return;
      var v = currentVariant();
      var mode = vol.getAttribute('data-mode');
      $$('[data-vol-input]', vol).forEach(function (inp) {
        var qty = parseInt(inp.getAttribute('data-qty'), 10) || 1;
        var disc = parseFloat(inp.getAttribute('data-discount')) || 0;
        var unit = v.price;
        if (disc > 0) unit = mode === 'percent' ? Math.round(v.price * (1 - disc / 100)) : Math.max(0, v.price - Math.round(disc * 100));
        var tier = inp.closest('.vol__tier');
        var priceEl = $('[data-vol-price]', tier);
        var cmpEl = $('[data-vol-compare]', tier);
        if (priceEl) priceEl.textContent = money(unit * qty);
        if (cmpEl) {
          cmpEl.hidden = !(disc > 0);
          cmpEl.textContent = money(v.price * qty);
        }
      });
    }
    if (vol) {
      var defaultTier = $('[data-vol-input]:checked', vol);
      refreshVolume();
      vol.addEventListener('change', function (e) {
        if (!e.target.matches('[data-vol-input]')) return;
        if (qtyInput) qtyInput.value = e.target.getAttribute('data-qty');
      });
      if (qtyInput) {
        if (defaultTier && (parseInt(qtyInput.value, 10) || 1) === 1) qtyInput.value = defaultTier.getAttribute('data-qty');
        qtyInput.addEventListener('change', function () {
          var match = false;
          $$('[data-vol-input]', vol).forEach(function (inp) {
            var is = inp.getAttribute('data-qty') === String(parseInt(qtyInput.value, 10));
            inp.checked = is;
            if (is) match = true;
          });
          if (!match) $$('[data-vol-input]', vol).forEach(function (inp) { inp.checked = false; });
        });
      }
    }

    // Delivery estimate
    $$('[data-delivery]', section).forEach(function (el) {
      var min = parseInt(el.getAttribute('data-min'), 10) || 0;
      var max = parseInt(el.getAttribute('data-max'), 10) || min;
      var skip = el.getAttribute('data-skip-weekends') === 'true';
      function addDays(n) {
        var d = new Date();
        var added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          if (!skip || (d.getDay() !== 0 && d.getDay() !== 6)) added++;
        }
        return d;
      }
      var fmt;
      try { fmt = new Intl.DateTimeFormat(root.lang || undefined, { month: 'short', day: 'numeric' }); } catch (e) { fmt = null; }
      var out = $('[data-delivery-range]', el);
      if (out && fmt) out.textContent = min === max ? fmt.format(addDays(min)) : fmt.format(addDays(min)) + ' – ' + fmt.format(addDays(max));
    });

    // Sticky add to cart bar
    var bar = $('[data-sticky-atc]', section);
    var mainBtn = $('[data-add-button]', section);
    if (bar && mainBtn && 'IntersectionObserver' in window) {
      var stickyBtn = $('[data-sticky-add]', bar);
      new IntersectionObserver(function (entries) {
        var en = entries[0];
        var show = !en.isIntersecting && en.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      }).observe(mainBtn);
    }

    if (gallery) initGallery(gallery);

    // Initial state from URL variant (silent: Liquid already rendered it)
    markAvailability();
    refreshVolume();

    // Recently viewed history (main product page only)
    if (!isQuick) {
      var hist = store('volt:recent') || [];
      hist = hist.filter(function (h) { return h !== product.handle; });
      hist.unshift(product.handle);
      store('volt:recent', hist.slice(0, 20));
    }
  }

  /* ---------- Gallery ---------- */
  function initGallery(g) {
    if (!once(g, 'gallery')) return;
    var track = $('[data-gallery-slides]', g);
    var slides = $$('.gallery-pdp__slide', g);
    var thumbs = $$('[data-thumb]', g);
    var counter = $('[data-gallery-counter]', g);
    var prev = $('[data-gallery-prev]', g);
    var next = $('[data-gallery-next]', g);
    var current = 0;

    function scrollable() { return track && track.scrollWidth > track.clientWidth + 4; }
    function setActive(i) {
      current = i;
      slides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
      thumbs.forEach(function (t, n) {
        t.classList.toggle('is-active', n === i);
        if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
      // pause videos that left the viewport
      $$('video', g).forEach(function (v) { if (!slides[i].contains(v)) v.pause(); });
    }
    function goTo(i, smooth) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      setActive(i);
      if (scrollable()) track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: smooth === false || reduceMotion ? 'auto' : 'smooth' });
    }
    g._showMedia = function (mediaId) {
      var idx = slides.findIndex(function (s) { return s.getAttribute('data-media-id') === mediaId; });
      if (idx > -1) goTo(idx);
    };
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });
    if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
    if (next) next.addEventListener('click', function () { goTo(current + 1); });
    if (track) {
      track.addEventListener('scroll', debounce(function () {
        if (!scrollable()) return;
        var best = 0, dist = Infinity;
        slides.forEach(function (s, n) {
          var d = Math.abs(s.offsetLeft - track.offsetLeft - track.scrollLeft);
          if (d < dist) { dist = d; best = n; }
        });
        if (best !== current) setActive(best);
      }, 80), { passive: true });
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { goTo(current - 1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { goTo(current + 1); e.preventDefault(); }
      });
    }

    // Hover zoom
    if (g.getAttribute('data-zoom') === 'true' && window.matchMedia('(hover: hover) and (min-width: 990px)').matches) {
      $$('.gallery-pdp__zoom.is-zoomable', g).forEach(function (z) {
        var img = $('img', z);
        if (!img) return;
        z.addEventListener('mouseenter', function () { z.classList.add('is-zooming'); });
        z.addEventListener('mousemove', function (e) {
          var r = z.getBoundingClientRect();
          img.style.transformOrigin = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
        });
        z.addEventListener('mouseleave', function () { z.classList.remove('is-zooming'); img.style.transformOrigin = ''; });
      });
    }

    // Lightbox
    var lb = $('[data-lightbox]', g);
    if (lb) {
      var lbImg = $('[data-lightbox-img]', lb);
      var items = slides.map(function (s) {
        var z = $('[data-zoom-src]', s);
        return z ? { src: z.getAttribute('data-zoom-src'), alt: z.getAttribute('data-zoom-alt') || '' } : null;
      });
      var pos = 0;
      var opener = null;
      function step(dir) {
        var n = items.length, idx = pos;
        for (var k = 0; k < n; k++) {
          idx = (idx + dir + n) % n;
          if (items[idx]) { pos = idx; break; }
        }
        lbImg.src = items[pos].src;
        lbImg.alt = items[pos].alt;
      }
      function open(i, trigger) {
        if (!items[i]) return;
        opener = trigger;
        pos = i;
        lbImg.src = items[i].src;
        lbImg.alt = items[i].alt;
        lb.hidden = false;
        lockScroll(true);
        $('[data-lightbox-close]', lb).focus();
      }
      lb._close = function () {
        lb.hidden = true;
        lbImg.src = '';
        lockScroll(false);
        if (opener) opener.focus();
      };
      $$('[data-lightbox-open]', g).forEach(function (b) {
        b.addEventListener('click', function () { open(parseInt(b.getAttribute('data-lightbox-open'), 10), b); });
      });
      $$('.gallery-pdp__zoom.is-zoomable', g).forEach(function (z) {
        z.addEventListener('click', function () {
          if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) return;
          var i = slides.indexOf(z.closest('.gallery-pdp__slide'));
          open(i, z);
        });
      });
      $('[data-lightbox-close]', lb).addEventListener('click', lb._close);
      $('[data-lightbox-prev]', lb).addEventListener('click', function () { step(-1); });
      $('[data-lightbox-next]', lb).addEventListener('click', function () { step(1); });
      lb.addEventListener('click', function (e) { if (e.target === lb || e.target.classList.contains('lightbox__stage')) lb._close(); });
      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') step(-1);
        if (e.key === 'ArrowRight') step(1);
        if (e.key === 'Tab') {
          var f = focusable(lb);
          if (!f.length) return;
          if (e.shiftKey && doc.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && doc.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      });
    }

    // 3D models (Shopify's model-viewer UI loads only for products that have a model)
    if (g.getAttribute('data-has-model') === 'true') {
      var tries = 0;
      (function bootModels() {
        if (window.Shopify && window.Shopify.ModelViewerUI) {
          $$('model-viewer', g).forEach(function (mv) {
            if (once(mv, 'mv')) mv._ui = new window.Shopify.ModelViewerUI(mv);
          });
        } else if (tries++ < 40) setTimeout(bootModels, 250);
      })();
    }
    setActive(0);
  }

  /* ---------- Bundle (frequently bought together) ---------- */
  function initBundles(ctx) {
    $$('[data-bundle]', ctx).forEach(function (b) {
      if (!once(b, 'bundle')) return;
      var items = $$('[data-bundle-item]', b);
      var totalEl = $('[data-bundle-total]', b);
      var addBtn = $('[data-bundle-add]', b);
      var errEl = $('[data-bundle-error]', b);
      function itemVariant(it) {
        var sel = $('[data-bundle-variant]', it);
        if (sel) {
          var opt = sel.options[sel.selectedIndex];
          return { id: sel.value, price: parseInt(opt.getAttribute('data-price'), 10) || 0 };
        }
        var fixed = $('[data-bundle-variant-fixed]', it);
        return { id: fixed.value, price: parseInt(fixed.getAttribute('data-price'), 10) || 0 };
      }
      function recalc() {
        var total = 0, count = 0;
        items.forEach(function (it) {
          var on = $('[data-bundle-check]', it).checked;
          it.classList.toggle('is-off', !on);
          var v = itemVariant(it);
          var p = $('[data-bundle-price]', it);
          if (p) p.textContent = money(v.price);
          if (on) { total += v.price; count++; }
        });
        if (totalEl) totalEl.textContent = (strings.bundleTotal || '') + ' ' + money(total);
        if (addBtn) addBtn.disabled = count === 0;
      }
      b.addEventListener('change', recalc);
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var list = [];
          items.forEach(function (it) {
            if ($('[data-bundle-check]', it).checked) list.push({ id: parseInt(itemVariant(it).id, 10), quantity: 1 });
          });
          if (!list.length) return;
          addBtn.classList.add('is-loading');
          if (errEl) errEl.hidden = true;
          cartRequest(routes.cartAdd, { items: list }).then(afterAdd).then(function () {
            addBtn.classList.remove('is-loading');
            announce(strings.added);
          }).catch(function (err) {
            addBtn.classList.remove('is-loading');
            if (errEl) { errEl.textContent = err.message || strings.error; errEl.hidden = false; }
          });
        });
      }
      recalc();
    });
  }

  /* ---------- Recommendations + recently viewed ---------- */
  function initRecommendations(ctx) {
    $$('[data-product-recommendations][data-url]', ctx).forEach(function (el) {
      if (!once(el, 'recs')) return;
      function load() {
        fetch(el.getAttribute('data-url')).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
          if (!html || !html.trim()) return;
          var fresh = $('[data-product-recommendations]', parseHTML(html));
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            initAll(el);
          }
        }).catch(function () { /* optional content */ });
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) {
          if (en[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '300px' });
        io.observe(el);
      } else load();
    });
  }

  function initRecentlyViewed(ctx) {
    $$('[data-recently-viewed]', ctx).forEach(function (el) {
      if (!once(el, 'rv')) return;
      var exclude = el.getAttribute('data-exclude');
      var limit = parseInt(el.getAttribute('data-limit'), 10) || 6;
      var handles = (store('volt:recent') || []).filter(function (h) { return h !== exclude; }).slice(0, limit);
      var list = $('[data-recently-viewed-list]', el);
      if (!handles.length || !list) return;
      Promise.all(handles.map(function (h) {
        return fetch('/products/' + encodeURIComponent(h) + '?view=card').then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
      })).then(function (cards) {
        var added = 0;
        cards.forEach(function (html) {
          if (!html || html.trim().indexOf('<article') !== 0 && html.indexOf('card') === -1) return;
          var li = doc.createElement('li');
          li.className = 'carousel__item';
          li.innerHTML = html;
          list.appendChild(li);
          added++;
        });
        if (added) { el.hidden = false; initAll(el); }
      });
    });
  }

  /* ---------- Quick view ---------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    var url;
    try { url = new URL(btn.getAttribute('data-quick-view'), window.location.origin); } catch (err) { return; }
    url.searchParams.set('view', 'quick');
    var target = $('[data-quick-view-content]');
    if (!target) { window.location.href = btn.getAttribute('data-quick-view'); return; }
    btn.classList.add('is-loading');
    fetch(url.toString()).then(function (r) { if (!r.ok) throw new Error('x'); return r.text(); }).then(function (html) {
      var d = parseHTML(html);
      var qv = $('[data-quick-view]', d);
      if (!qv) throw new Error('x');
      target.innerHTML = qv.outerHTML;
      btn.classList.remove('is-loading');
      openDrawer('quickview', btn);
      initAll(target);
    }).catch(function () {
      btn.classList.remove('is-loading');
      window.location.href = btn.getAttribute('data-quick-view');
    });
  });
  /* ---------- Predictive search ---------- */
  function initSearch() {
    $$('[data-predictive-search]').forEach(function (box) {
      if (!once(box, 'psearch')) return;
      var input = $('[data-search-input]', box);
      var results = $('[data-search-results]', box);
      var def = $('[data-search-default]', box);
      var enabled = box.getAttribute('data-enabled') !== 'false';
      var limit = box.getAttribute('data-limit') || 4;
      var ctrl = null;
      if (!input || !results || !enabled) return;
      function reset() {
        results.innerHTML = '';
        if (def) def.hidden = false;
        box.classList.remove('has-results');
      }
      var run = debounce(function () {
        var q = input.value.trim();
        if (!q) { reset(); return; }
        if (ctrl) ctrl.abort();
        ctrl = window.AbortController ? new AbortController() : null;
        results.classList.add('is-loading');
        var url = routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&section_id=predictive-search&resources[limit]=' + limit + '&resources[options][unavailable_products]=last';
        fetch(url, ctrl ? { signal: ctrl.signal } : {}).then(function (r) { if (!r.ok) throw new Error('x'); return r.text(); }).then(function (html) {
          var d = parseHTML(html);
          var node = $('.psearch', d);
          results.innerHTML = node ? node.outerHTML : '';
          results.classList.remove('is-loading');
          if (def) def.hidden = true;
          box.classList.add('has-results');
          var count = node ? node.getAttribute('data-psearch-count') : '0';
          announce(count === '0' ? strings.noResults : '');
        }).catch(function (err) {
          if (err && err.name === 'AbortError') return;
          results.classList.remove('is-loading');
        });
      }, 250);
      input.addEventListener('input', run);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          var first = $('a[href]', results);
          if (first) { e.preventDefault(); first.focus(); }
        }
      });
      results.addEventListener('keydown', function (e) {
        var links = $$('a[href]', results);
        var i = links.indexOf(doc.activeElement);
        if (e.key === 'ArrowDown' && i > -1) { e.preventDefault(); (links[i + 1] || links[i]).focus(); }
        if (e.key === 'ArrowUp' && i > -1) { e.preventDefault(); if (i === 0) input.focus(); else links[i - 1].focus(); }
      });
    });
  }

  /* ==========================================================================
     COLLECTION: ajax filters, sort, load more, infinite scroll
     ========================================================================== */
  function getCollection() { return $('[data-collection]'); }
  function openFacets() {
    var f = $('#FacetsForm');
    var ov = $('[data-facets-overlay]');
    if (!f) return;
    f.classList.add('is-open');
    if (ov) { ov.hidden = false; requestAnimationFrame(function () { ov.classList.add('is-visible'); }); }
    lockScroll(true);
    var c = $('[data-facets-close]', f);
    if (c) c.focus();
  }
  function closeFacets() {
    var f = $('#FacetsForm');
    var ov = $('[data-facets-overlay]');
    if (f) f.classList.remove('is-open');
    if (ov) { ov.classList.remove('is-visible'); setTimeout(function () { ov.hidden = true; }, 350); }
    lockScroll(false);
    var o = $('[data-facets-open]');
    if (o) o.focus();
  }
  doc.addEventListener('click', function (e) {
    if (e.target.closest('[data-facets-open]')) { openFacets(); return; }
    if (e.target.closest('[data-facets-close]')) { closeFacets(); return; }
    if (e.target.matches('[data-facets-overlay]')) closeFacets();
  });

  function facetsUrl(form) {
    var fd = new FormData(form);
    var params = new URLSearchParams();
    fd.forEach(function (v, k) { if (v !== '' && k !== 'page') params.append(k, v); });
    var sort = $('[data-sort-select]');
    if (sort && sort.value && !params.has('sort_by')) params.append('sort_by', sort.value);
    var q = params.toString();
    return window.location.pathname + (q ? '?' + q : '');
  }

  function loadCollection(url, push) {
    var col = getCollection();
    if (!col) { window.location.href = url; return; }
    var sectionId = col.getAttribute('data-section-id');
    col.classList.add('is-loading');
    var open = $('#FacetsForm') && $('#FacetsForm').classList.contains('is-open');
    var openFacetsState = $$('[data-facet]').map(function (d) { return d.open; });
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId)).then(function (r) { if (!r.ok) throw new Error('x'); return r.text(); }).then(function (html) {
      var d = parseHTML(html);
      var results = $('[data-collection-results]', d);
      var facets = $('[data-facets-container]', d);
      var count = $('[data-results-count]', d);
      if (results) $('[data-collection-results]').innerHTML = results.innerHTML;
      var fc = $('[data-facets-container]');
      if (facets && fc) {
        fc.innerHTML = facets.innerHTML;
        $$('[data-facet]', fc).forEach(function (det, i) { if (openFacetsState[i] !== undefined) det.open = openFacetsState[i]; });
        if (open) { var nf = $('#FacetsForm'); if (nf) nf.classList.add('is-open'); }
      }
      if (count && $('[data-results-count]')) $('[data-results-count]').textContent = count.textContent;
      if (push !== false && window.history.pushState) window.history.pushState({ volt: true }, '', url);
      col.classList.remove('is-loading');
      initAll(col);
      if (count) announce(count.textContent);
      if (!open) {
        var top = col.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 72) - 16;
        if (window.scrollY > top) window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      initLoadMore();
    }).catch(function () {
      window.location.href = url;
    });
  }

  var facetChange = debounce(function (form) { loadCollection(facetsUrl(form)); }, 350);
  doc.addEventListener('change', function (e) {
    var form = e.target.closest('[data-facets-form]');
    if (form && e.target.matches('input, select')) { facetChange(form); return; }
    if (e.target.matches('[data-sort-select]')) {
      var f = $('#FacetsForm');
      if (f) loadCollection(facetsUrl(f));
      else { var u = new URL(window.location.href); u.searchParams.set('sort_by', e.target.value); u.searchParams.delete('page'); window.location.href = u.toString(); }
    }
  });
  doc.addEventListener('submit', function (e) {
    if (e.target.matches('[data-facets-form]')) { e.preventDefault(); loadCollection(facetsUrl(e.target)); }
  });
  doc.addEventListener('click', function (e) {
    var link = e.target.closest('[data-facet-link]');
    if (!link || !getCollection()) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    loadCollection(link.getAttribute('href'));
  });
  window.addEventListener('popstate', function (e) {
    if (getCollection() && e.state && e.state.volt) loadCollection(window.location.pathname + window.location.search, false);
  });

  // Load more / infinite scroll
  var infiniteObserver = null;
  function loadMore(link) {
    var col = getCollection();
    if (!col || link._busy) return;
    link._busy = true;
    link.classList.add('is-loading');
    var sectionId = col.getAttribute('data-section-id');
    var href = link.getAttribute('href');
    var sep = href.indexOf('?') > -1 ? '&' : '?';
    fetch(href + sep + 'section_id=' + encodeURIComponent(sectionId)).then(function (r) { if (!r.ok) throw new Error('x'); return r.text(); }).then(function (html) {
      var d = parseHTML(html);
      var grid = $('[data-product-grid]');
      var newItems = $$('[data-product-grid] > li', d);
      newItems.forEach(function (li) { grid.appendChild(doc.importNode(li, true)); });
      var wrap = $('[data-load-more]');
      var newWrap = $('[data-load-more]', d);
      if (wrap) {
        if (newWrap) wrap.innerHTML = newWrap.innerHTML; else wrap.remove();
      }
      initAll(grid);
      if (window.history.replaceState) window.history.replaceState({ volt: true }, '', window.location.href);
      initLoadMore();
    }).catch(function () {
      link._busy = false;
      link.classList.remove('is-loading');
      window.location.href = href;
    });
  }
  doc.addEventListener('click', function (e) {
    var link = e.target.closest('[data-load-more-link]');
    if (!link) return;
    e.preventDefault();
    loadMore(link);
  });
  function initLoadMore() {
    var col = getCollection();
    if (!col || col.getAttribute('data-pagination') !== 'infinite' || !('IntersectionObserver' in window)) return;
    var link = $('[data-load-more-link]');
    if (infiniteObserver) infiniteObserver.disconnect();
    if (!link) return;
    infiniteObserver = new IntersectionObserver(function (en) {
      if (en[0].isIntersecting) { infiniteObserver.disconnect(); loadMore(link); }
    }, { rootMargin: '600px' });
    infiniteObserver.observe(link);
  }

  /* ---------- Toast (optional, merchant-written, disabled by default) ---------- */
  function initToast() {
    var toast = $('[data-toast]');
    if (!toast || !once(toast, 'toast')) return;
    var dismissed = false;
    try { dismissed = sessionStorage.getItem('volt:toast-dismissed') === '1'; } catch (e) { /* ignore */ }
    if (dismissed) return;
    var msgs = $$('[data-toast-message]', toast);
    if (!msgs.length) return;
    var first = parseInt(toast.getAttribute('data-first-delay'), 10) || 8000;
    var visible = parseInt(toast.getAttribute('data-visible-time'), 10) || 6000;
    var gap = parseInt(toast.getAttribute('data-interval'), 10) || 20000;
    var i = 0, stopped = false;
    function show() {
      if (stopped) return;
      msgs.forEach(function (m, n) { m.hidden = n !== i; });
      toast.hidden = false;
      setTimeout(hide, visible);
    }
    function hide() {
      toast.hidden = true;
      i = (i + 1) % msgs.length;
      if (!stopped) setTimeout(show, gap);
    }
    $('[data-toast-close]', toast).addEventListener('click', function () {
      stopped = true;
      toast.hidden = true;
      try { sessionStorage.setItem('volt:toast-dismissed', '1'); } catch (e) { /* ignore */ }
    });
    setTimeout(show, first);
  }

  /* ---------- Misc ---------- */
  function initBackToTop() {
    var btn = $('[data-back-to-top]');
    if (!btn || !once(btn, 'btt')) return;
    window.addEventListener('scroll', debounce(function () {
      btn.classList.toggle('is-visible', window.scrollY > 700);
    }, 50), { passive: true });
    btn.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }); });
  }

  doc.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-localization-select]');
    if (sel && sel.form) sel.form.submit();
  });

  doc.addEventListener('click', function (e) {
    var copy = e.target.closest('[data-copy-link]');
    if (copy) {
      var orig = copy.textContent;
      var done = function () { copy.textContent = copy.getAttribute('data-copied'); setTimeout(function () { copy.textContent = orig; }, 2000); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(window.location.href).then(done);
      else {
        var ta = doc.createElement('textarea');
        ta.value = window.location.href; body.appendChild(ta); ta.select();
        try { doc.execCommand('copy'); done(); } catch (err) { /* ignore */ }
        ta.remove();
      }
      return;
    }
    var auth = e.target.closest('[data-auth-show]');
    if (auth) {
      var which = auth.getAttribute('data-auth-show');
      var wrap = auth.closest('[data-auth]');
      var login = $('[data-auth-login]', wrap), rec = $('[data-auth-recover]', wrap);
      login.hidden = which !== 'login';
      rec.hidden = which !== 'recover';
      var f = $('input', which === 'login' ? login : rec);
      if (f) f.focus();
      return;
    }
    var addr = e.target.closest('[data-address-toggle]');
    if (addr) {
      var panel = doc.getElementById(addr.getAttribute('data-address-toggle'));
      if (panel) {
        panel.hidden = !panel.hidden;
        addr.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
      }
    }
  });

  function initAuth() {
    var wrap = $('[data-auth]');
    if (wrap && window.location.hash === '#recover') {
      $('[data-auth-login]', wrap).hidden = true;
      $('[data-auth-recover]', wrap).hidden = false;
    }
  }

  // Country -> province selects
  function initProvinces(ctx) {
    $$('select[name="address[country]"]', ctx).forEach(function (country) {
      if (!once(country, 'prov')) return;
      var fieldset = country.closest('form') || doc;
      var wrap = $('[data-province-wrap]', fieldset);
      var prov = wrap && $('select', wrap);
      if (!prov) return;
      function sync(initial) {
        var opt = country.options[country.selectedIndex];
        var list = [];
        try { list = JSON.parse((opt && opt.getAttribute('data-provinces')) || '[]'); } catch (e) { list = []; }
        prov.innerHTML = '';
        if (!list.length) { wrap.hidden = true; return; }
        list.forEach(function (p) {
          var o = doc.createElement('option');
          o.value = p[0]; o.textContent = p[1];
          prov.appendChild(o);
        });
        wrap.hidden = false;
        var def = prov.getAttribute('data-default');
        if (initial && def) prov.value = def;
      }
      var cdef = country.getAttribute('data-default');
      if (cdef) {
        for (var i = 0; i < country.options.length; i++) {
          if (country.options[i].value === cdef || country.options[i].text === cdef) { country.selectedIndex = i; break; }
        }
      }
      country.addEventListener('change', function () { sync(false); });
      sync(true);
    });
  }

  // Video hero (YouTube / Vimeo load only after the visitor clicks play)
  function initVideoHero(ctx) {
    $$('[data-video-hero]', ctx).forEach(function (hero) {
      var play = $('[data-video-play]', hero);
      var frame = $('[data-video-frame]', hero);
      if (!play || !frame || !once(hero, 'vh')) return;
      var embed = $('[data-video-embed]', frame);
      frame._close = function () {
        frame.hidden = true;
        embed.innerHTML = '';
        play.focus();
      };
      play.addEventListener('click', function () {
        var id = play.getAttribute('data-video-id');
        var type = play.getAttribute('data-video-type');
        var src = type === 'vimeo' ? 'https://player.vimeo.com/video/' + encodeURIComponent(id) + '?autoplay=1' : 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
        var iframe = doc.createElement('iframe');
        iframe.src = src;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = play.textContent.trim();
        embed.innerHTML = '';
        embed.appendChild(iframe);
        frame.hidden = false;
        $('[data-video-close]', frame).focus();
      });
      $('[data-video-close]', frame).addEventListener('click', frame._close);
    });
  }

  /* ---------- Init ---------- */
  function initAll(ctx) {
    ctx = ctx || doc;
    initSlideshows(ctx);
    initCarousels(ctx);
    initTabs(ctx);
    initCountdowns(ctx);
    initReveal(ctx);
    initParallax(ctx);
    initBundles(ctx);
    initRecommendations(ctx);
    initRecentlyViewed(ctx);
    initProvinces(ctx);
    initVideoHero(ctx);
    $$('[data-product-section]', ctx).forEach(initProduct);
    if (ctx.matches && ctx.matches('[data-product-section]')) initProduct(ctx);
  }

  function boot() {
    initHeader();
    initNav();
    initAll(doc);
    initShipBars();
    initUpsells();
    initSearch();
    initLoadMore();
    initToast();
    initBackToTop();
    initAuth();
    if (!revealObserver) $$('[data-reveal]').forEach(function (el) { el.classList.add('is-revealed'); });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ---------- Theme editor support ---------- */
  doc.addEventListener('shopify:section:load', function (e) {
    initHeader();
    initNav();
    initAll(e.target);
    initShipBars();
    initSearch();
    initLoadMore();
    initToast();
  });
  doc.addEventListener('shopify:block:select', function (e) {
    var t = e.target;
    var show = t.closest('[data-slideshow], [data-announcement]');
    if (show && show._slideshow) {
      var idx = show._slideshow.slides.indexOf(t.closest('[data-slide], [data-announcement-slide]'));
      if (idx > -1) show._slideshow.go(idx, true);
    }
    var tabs = t.closest('[data-tabs]');
    if (tabs && tabs._tabs && t.classList.contains('tabs__tab')) tabs._tabs.activate($$('.tabs__tab', tabs).indexOf(t));
  });

  V.openDrawer = openDrawer;
  V.closeDrawer = closeDrawer;
  V.refreshCart = refreshCart;
  V.formatMoney = money;
})();
